import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import {
  DM_ONBOARDING_CARD_IDS,
  ONBOARDING_CARD_IDS,
  type OnboardingCardId,
  hasOnboardingDeckClaim,
} from '../../lib/onboardingCardDismissal';
import { LATEST_JUMP_REQUESTED_EVENT, consumeLatestJump } from '../../lib/pendingLatestJump';
import { consumeMessageJump, restoreMessageJump } from '../../lib/pendingMessageJump';

import { EntitySnapshotContext } from '../../contexts/EntitySnapshotContext';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { sortOrderFromString } from '../core/chatItem';
import { FeedController } from '../core/controller';
import { EntityStore } from '../core/entityStore';
import { FeedSession, type FeedSessionServer } from '../core/session';
import type { ChatItem, SortOrder } from '../core/types';
import { EchoFeedApi } from '../echo/api';
import { claimActiveFeedChannel, getFeedBuffer, registerBufferHealthListener } from '../echo/feedCache';
import { createEchoItemHeightEstimator } from '../echo/itemHeightEstimator';
import { type EchoProfileStore, createEchoProfileStore } from '../echo/profileStore';
import { FeedRestoreStorage } from '../echo/restore';
import { EchoSocketAdapter } from '../echo/socket';
import { type FeedEngine, createStaticFeedEngine } from '../engine';
import {
  endOfMessagesHeightContextKey,
  invalidateEndOfMessagesHeights,
  scheduleEndOfMessagesHeightInvalidation,
  subscribeEndOfMessagesHeightDismissal,
} from './endOfMessagesHeightInvalidation';
import { type EntitySnapshotMaps, entityBundleFromSnapshots } from './entityBundle';
import { reportFeedEngineError } from './feedEngineErrors';
import { markFeedSessionPerformanceEvent } from './feedPerformance';
import { reportFeedRecoveryEvent } from './feedRecoveryTelemetry';
import { drainPendingLatestJump, handleFeedLatestJumpRequest } from './latestJump';
import { drainPendingMessageJump } from './messageJump';
import { subscribeFeedSessionLifecycleEvents } from './sessionLifecycleFreshness';

const OVERSCAN_PX = 1200;
const PAGE_SIZE = 80;
const EDGE_PAGE_THRESHOLD_PX = 2400;

// Session identity for shared-channel viewers with no workspace profile (anonymous
// visitors and signed-in non-members). It matches no real profile id, so own-message
// and reactedByMe checks are always false, while the route-level profileId stays null
// and keeps the composer and read cursors disabled.
const SHARED_CHANNEL_ANONYMOUS_VIEWER_ID = 'anon';

interface FeedLifecycleChannel {
  readonly id?: string;
  readonly kind?: string;
  readonly oldest_message_sort_order?: string;
}

interface FeedLifecycleRouteWorkspace {
  readonly directMessage?: { readonly last_message_sort_order?: string | null } | null;
  readonly isDirectMessage?: boolean;
  readonly mainChannelId?: string | null;
  readonly profile?: { readonly role?: string } | null;
  readonly profileId?: string | null;
  readonly workspaceId?: string | null;
}

/** Session, controller, engine, and entity-store ownership for the MessageFeed shell. */
export interface FeedSessionLifecycle {
  readonly channelId: string | null;
  readonly controller: FeedController;
  readonly engine: FeedEngine<ChatItem>;
  readonly entitySnapshotMaps: EntitySnapshotMaps | null | undefined;
  readonly profileId: string | null;
  readonly session: FeedSession | null;
}

/** Builds and runs the feed session graph for the active route channel. */
export function useFeedSessionLifecycle(
  currentChannel: FeedLifecycleChannel | null | undefined,
  onMessageJumpComplete?: (sortOrder: SortOrder) => void,
  isSharedChannel = false,
): FeedSessionLifecycle {
  const entitySnapshots = useContext(EntitySnapshotContext);
  // SAFETY: EntitySnapshotContext publishes the map shape consumed by feed entity enrichment.
  const entitySnapshotMaps = entitySnapshots as EntitySnapshotMaps | null | undefined;
  const {
    directMessage,
    isDirectMessage = false,
    mainChannelId,
    profile,
    profileId: routeProfileId,
    workspaceId,
  } = useRouteWorkspace() as FeedLifecycleRouteWorkspace;
  const profileId = routeProfileId ?? null;
  // Shared channels are viewable without a workspace profile; everywhere else the
  // session waits for the viewer's profile to resolve.
  const sessionViewerId = profileId ?? (isSharedChannel ? SHARED_CHANNEL_ANONYMOUS_VIEWER_ID : null);
  const viewerProfileIdRef = useRef(profileId);
  viewerProfileIdRef.current = profileId;
  const endOfMessagesContextRef = useRef({
    hasCurrentChannel: false,
    isMainOrSharedChannel: false,
    isOpenBreakout: false,
    isOnboardingAdmin: false,
    onboardingCardIds: ONBOARDING_CARD_IDS as readonly OnboardingCardId[],
    profileId,
    workspaceId: null as string | null,
  });
  const isMainChannel = Boolean(currentChannel?.id && mainChannelId && String(currentChannel.id) === String(mainChannelId));
  const endOfMessagesContext = {
    hasCurrentChannel: Boolean(currentChannel),
    isMainOrSharedChannel: isMainChannel || isSharedChannel,
    isOpenBreakout: currentChannel?.kind === 'open_breakout',
    // Mirrors EndOfMessages: the deck belongs to the creator alone — groups claim at
    // creation, DMs claim by being seen empty (the peer only ever sees a revealed DM).
    isOnboardingAdmin:
      !isSharedChannel &&
      profile?.role === 'admin' &&
      (isDirectMessage
        ? !directMessage?.last_message_sort_order || hasOnboardingDeckClaim(workspaceId ? String(workspaceId) : null, profileId)
        : hasOnboardingDeckClaim(workspaceId ? String(workspaceId) : null, profileId)),
    onboardingCardIds: (isDirectMessage ? DM_ONBOARDING_CARD_IDS : ONBOARDING_CARD_IDS) as readonly OnboardingCardId[],
    profileId,
    workspaceId: workspaceId ? String(workspaceId) : null,
  };
  endOfMessagesContextRef.current = endOfMessagesContext;
  const sessionRef = useRef<FeedSession | null>(null);
  const sessionViewerIdRef = useRef<string | null>(null);
  const activeSessionChannelRef = useRef<string | null>(null);
  const readyConversationRef = useRef<{ channelId: string | null; promise: Promise<void> }>({
    channelId: null,
    promise: Promise.resolve(),
  });
  const estimateHeight = useMemo(
    () => createEchoItemHeightEstimator(viewerProfileIdRef, endOfMessagesContextRef),
    [endOfMessagesContextRef, viewerProfileIdRef],
  );
  const engine = useMemo(
    () =>
      createStaticFeedEngine<ChatItem>({
        estimateHeight,
        onError: reportFeedEngineError,
        overscanPx: OVERSCAN_PX,
      }),
    [estimateHeight],
  );
  const controller = useMemo(() => new FeedController({ engine }), [engine]);
  const endOfMessagesContextKey = endOfMessagesHeightContextKey(endOfMessagesContext);
  const invalidateEndOfMessagesHeightNow = useCallback(() => {
    invalidateEndOfMessagesHeights(controller.snapshot().items, (key) => engine.invalidateItemHeight(key));
  }, [controller, engine]);
  const buffer = useMemo(() => getFeedBuffer(), []);
  const restoreStorage = useMemo(() => new FeedRestoreStorage(), []);
  const entityStore = useMemo(() => (sessionViewerId ? new EntityStore({ viewerProfileId: sessionViewerId }) : null), [sessionViewerId]);
  const handlePendingMessageJumpComplete = useCallback(
    (input: { readonly sortOrder: SortOrder }) => {
      onMessageJumpComplete?.(input.sortOrder);
    },
    [onMessageJumpComplete],
  );
  // The session is constructed once, so its callbacks must reach the latest
  // jump-complete handler through a ref rather than a stale closure.
  const handlePendingMessageJumpCompleteRef = useRef(handlePendingMessageJumpComplete);
  handlePendingMessageJumpCompleteRef.current = handlePendingMessageJumpComplete;
  const channelId = currentChannel?.id ? String(currentChannel.id) : null;
  const oldestMessageSortOrder = currentChannel?.oldest_message_sort_order;
  const canStartSession = Boolean(channelId && sessionViewerId && entityStore);
  const scheduleCurrentEndOfMessagesHeightInvalidation = useCallback(() => {
    const targetChannelId = channelId;
    const readyConversation = readyConversationRef.current;
    if (!targetChannelId || readyConversation.channelId !== targetChannelId) return () => {};

    return scheduleEndOfMessagesHeightInvalidation(
      readyConversation.promise,
      () => activeSessionChannelRef.current === targetChannelId,
      invalidateEndOfMessagesHeightNow,
    );
  }, [channelId, invalidateEndOfMessagesHeightNow]);

  // A session is keyed to the identity it booted under (accountId, viewerProfileId,
  // entity store). That identity can change while mounted: on shared pages a signed-in
  // member boots as the anonymous viewer first, then re-keys when the as-user shell
  // bootstrap resolves their profile. Discard the stale session so the block below
  // rebuilds it; the start/stop effect's cleanup stops the discarded session when the
  // replacement lands in its deps.
  if (sessionRef.current && sessionViewerIdRef.current !== sessionViewerId) {
    sessionRef.current = null;
    activeSessionChannelRef.current = null;
  }

  if (!sessionRef.current && canStartSession && channelId && sessionViewerId && entityStore) {
    const server = createEchoFeedSessionServer(channelId, oldestMessageSortOrder);
    const session = new FeedSession({
      accountId: sessionViewerId,
      // Boot straight into a pending jump's around-target window so the first
      // paint is already centered on the message (no latest paint, no visible
      // re-scroll). Applies to both fresh mounts and conversation switches.
      bootMessageJump: (conversationId) => {
        const pendingSortOrder = consumeMessageJump(conversationId);
        const sortOrder = pendingSortOrder ? sortOrderFromString(pendingSortOrder) : undefined;
        return sortOrder;
      },
      buffer,
      callbacks: {
        onBootMessageJumpAbandoned: (conversationId, sortOrder) => {
          restoreMessageJump({ channelId: conversationId, sortOrder: String(sortOrder) });
        },
        onBootMessageJumpInstalled: (conversationId, sortOrder) => {
          // The landed boot jump consumed the message-jump buffer before the
          // post-boot drain ran, so the drain's "message jump wins" precedence
          // can't see it. Apply the precedence here: discard any queued latest
          // jump for the channel so it cannot undo the boot-time centering.
          consumeLatestJump(conversationId);
          handlePendingMessageJumpCompleteRef.current({ sortOrder });
        },
        onDebugEvent: (kind, data) => {
          markFeedSessionPerformanceEvent(kind);
          reportFeedRecoveryEvent(kind, data);
        },
      },
      controller,
      conversationId: channelId,
      edgePageThresholdPx: EDGE_PAGE_THRESHOLD_PX,
      engine,
      entityStore,
      pageSize: PAGE_SIZE,
      restoreStorage,
      server,
      viewerProfileId: sessionViewerId,
    });
    sessionRef.current = session;
    sessionViewerIdRef.current = sessionViewerId;
    activeSessionChannelRef.current = channelId;
  }

  const session = canStartSession ? sessionRef.current : null;
  const jumpToLatestWhenReady = useCallback(
    (targetChannelId: string) => {
      if (!session) return;

      const readyConversation = readyConversationRef.current;
      const readyPromise = readyConversation.channelId === targetChannelId ? readyConversation.promise : Promise.resolve();
      void readyPromise
        .then(() => {
          if (session.getSnapshot().conversationId !== targetChannelId) return;
          return session.jumpToLatest();
        })
        .catch((error: unknown) => {
          session.setStatus(`Feed latest jump failed: ${describeError(error)}`);
        });
    },
    [session],
  );

  useEffect(() => {
    if (!entityStore) return;
    const bundle = entityBundleFromSnapshots(entitySnapshotMaps);
    if (!session) {
      entityStore.ingest(bundle);
      return;
    }

    // Entity fan-out can publish a render window whose external-store bridge
    // flushes synchronously. Let this React commit finish first so that flush is
    // real (rather than React's lifecycle no-op); boot still resumes afterward.
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) session.applyEntityBundle(bundle);
    });
    return () => {
      cancelled = true;
    };
  }, [entitySnapshotMaps, entityStore, session]);

  useEffect(() => {
    if (!session) return undefined;

    // A superseded start()'s promise still resolves after cleanup runs (the
    // session's token guards no-op its installs but never reject), so without
    // this flag a stale callback would consume the pending jump and race the
    // successor's boot. Bail instead; the successor's own drain picks it up.
    let cancelled = false;
    const sessionChannelId = session.getSnapshot().conversationId || null;
    const startPromise = session.start();
    readyConversationRef.current = { channelId: sessionChannelId, promise: startPromise };
    void startPromise
      .then(() => {
        if (cancelled) return;

        // Drain only after boot settles. Draining synchronously alongside
        // start() raced the boot's window installs: they bump the window
        // epoch, so the jump's around-fetch was dropped as stale and the
        // user landed at the boot position instead of the target message.
        // A drained message jump also suppresses the latest-jump drain —
        // jumping to latest right after centering the target would undo it.
        const drainedMessageJump = drainPendingMessageJump(session, sessionChannelId, consumeMessageJump, {
          onJumpComplete: handlePendingMessageJumpComplete,
        });
        if (sessionChannelId && !drainedMessageJump) {
          drainPendingLatestJump(sessionChannelId, consumeLatestJump, () => jumpToLatestWhenReady(sessionChannelId));
        }
      })
      .catch((error: unknown) => {
        session.setStatus(`Feed session start failed: ${describeError(error)}`);
      });
    return () => {
      cancelled = true;
      session.stop();
    };
  }, [handlePendingMessageJumpComplete, jumpToLatestWhenReady, session]);

  useEffect(() => {
    if (!session || !channelId) return undefined;
    const activeChannelId = channelId;

    function handleLatestJumpRequested(event: Event) {
      const detail = 'detail' in event ? event.detail : undefined;
      handleFeedLatestJumpRequest(activeChannelId, detail, consumeLatestJump, () => jumpToLatestWhenReady(activeChannelId));
    }

    window.addEventListener(LATEST_JUMP_REQUESTED_EVENT, handleLatestJumpRequested);
    return () => {
      window.removeEventListener(LATEST_JUMP_REQUESTED_EVENT, handleLatestJumpRequested);
    };
  }, [channelId, jumpToLatestWhenReady, session]);

  useEffect(() => registerBufferHealthListener((healthy) => sessionRef.current?.updateBufferHealth(healthy)), []);

  useEffect(() => {
    if (!channelId) return undefined;
    return claimActiveFeedChannel(channelId);
  }, [channelId]);

  useEffect(() => {
    if (!session) return undefined;
    return subscribeFeedSessionLifecycleEvents(session);
  }, [session]);

  useEffect(() => {
    if (!session || !channelId) return;
    if (activeSessionChannelRef.current === channelId) {
      session.updateConversationStartSortOrder(parseConversationStartSortOrder(oldestMessageSortOrder));
      return;
    }

    activeSessionChannelRef.current = channelId;
    const switchPromise = session.switchConversation(channelId, createEchoFeedSessionServer(channelId, oldestMessageSortOrder));
    readyConversationRef.current = { channelId, promise: switchPromise };
    void switchPromise
      .then(() => {
        // A newer switch owns the session now: a superseded switch resolves
        // without rejecting, and draining here would consume this channel's
        // jump and replay it against whatever conversation is active instead.
        if (activeSessionChannelRef.current !== channelId) return;

        // Same precedence as the mount path: a drained message jump wins over
        // a pending latest jump for the same channel.
        const drainedMessageJump = drainPendingMessageJump(session, channelId, consumeMessageJump, {
          onJumpComplete: handlePendingMessageJumpComplete,
        });
        if (!drainedMessageJump) {
          drainPendingLatestJump(channelId, consumeLatestJump, () => jumpToLatestWhenReady(channelId));
        }
      })
      .catch((error: unknown) => {
        // A failed switch leaves the session on the previous conversation; clearing the ref
        // lets the next channel change (or a retry render) attempt the switch again.
        if (activeSessionChannelRef.current === channelId) {
          activeSessionChannelRef.current = null;
        }
        session.setStatus(`Feed conversation switch failed: ${describeError(error)}`);
      });
  }, [channelId, handlePendingMessageJumpComplete, jumpToLatestWhenReady, oldestMessageSortOrder, session]);

  // These effects intentionally follow the conversation switch effect above:
  // it first publishes the target channel's readiness promise, then contextual
  // invalidation waits for that promise before reading the installed window.
  useEffect(
    () => scheduleCurrentEndOfMessagesHeightInvalidation(),
    [endOfMessagesContextKey, scheduleCurrentEndOfMessagesHeightInvalidation],
  );
  useEffect(() => {
    let cancelPendingInvalidation = () => {};
    const unsubscribe = subscribeEndOfMessagesHeightDismissal(() => {
      cancelPendingInvalidation();
      cancelPendingInvalidation = scheduleCurrentEndOfMessagesHeightInvalidation();
    });
    return () => {
      cancelPendingInvalidation();
      unsubscribe();
    };
  }, [scheduleCurrentEndOfMessagesHeightInvalidation]);

  return {
    channelId,
    controller,
    engine,
    entitySnapshotMaps,
    profileId,
    session,
  };
}

function createEchoFeedSessionServer(channelId: string, oldestMessageSortOrder?: string): FeedSessionServer {
  const profileStore = echoProfileStore();
  const api = new EchoFeedApi(channelId, { profileStore });
  const socket = new EchoSocketAdapter(channelId);
  const conversationStartSortOrder = parseConversationStartSortOrder(oldestMessageSortOrder);
  return {
    ...(conversationStartSortOrder === undefined ? {} : { conversationStartSortOrder }),
    latest: (limit?: number) => api.latest(limit),
    before: (cursor, limit?: number) => api.before(cursor, limit),
    after: (cursor, limit?: number) => api.after(cursor, limit),
    around: (anchor, limit?: number) => api.around(anchor, limit),
    sync: (cursor: string) => api.sync(cursor),
    subscribe: (cb) => socket.subscribe(cb),
    onLocalOptimistic: (cb) => socket.onLocalOptimistic(cb),
    onReactionDelta: (cb) => socket.onReactionDelta(cb),
    send: (clientRequestId, body) => socket.send(clientRequestId, body),
    sourceLabel: 'echo',
  };
}

function echoProfileStore(): EchoProfileStore {
  return createEchoProfileStore();
}

function parseConversationStartSortOrder(oldestMessageSortOrder?: string): SortOrder | undefined {
  return oldestMessageSortOrder ? sortOrderFromString(oldestMessageSortOrder) : undefined;
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string' && error.length > 0) return error;
  return 'unhandled failure';
}
